import type { MantineTheme } from '@mantine/core';
import { Anchor, Group, Stack, Textarea } from '@mantine/core';
import { useClipboard } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import { Button, Text } from '@repo/foundations';
import ed25519 from 'ed25519-keygen/ssh';
import { randomBytes } from 'ed25519-keygen/utils';
import { useCallback, useState } from 'react';
import type { ITunnel } from '../../../api';
import { queryClient, tunnelsQueryKeyFactory } from '../../../api';
import { closeAllModals } from '../../../components/ModalManager';
import { TUNNEL_TEMPLATE } from '../constants';

interface CreateReverseTunnelModalProps {
	onCreateTunnel: (params: { data: Partial<ITunnel> }) => Promise<ITunnel>;
	tunnelUrl?: string;
	theme: MantineTheme;
}

type ModalState = 'initial' | 'loading' | 'success' | 'error';

interface TunnelResult {
	tunnel: ITunnel;
	template: string;
}

function CreateReverseTunnelModal({
	onCreateTunnel,
	tunnelUrl,
	theme,
}: CreateReverseTunnelModalProps) {
	const clipboard = useClipboard();
	const [modalState, setModalState] = useState<ModalState>('initial');
	const [tunnelResult, setTunnelResult] = useState<TunnelResult | null>(null);
	const [errorMessage, setErrorMessage] = useState<string>('');

	const handleCreateTunnel = useCallback(async () => {
		try {
			setModalState('loading');
			setErrorMessage('');

			// Generate cryptographic keys
			const seed = randomBytes(32);
			const keys = await ed25519(seed, 'ssh@secoda.co');

			// Create the tunnel
			const tunnel = await onCreateTunnel({
				data: {
					public_key: keys.publicKey,
					reverse: true,
				},
			});

			// Invalidate related queries
			queryClient.invalidateQueries({
				queryKey: tunnelsQueryKeyFactory.list(1, { reverse: true }),
			});

			// Generate configuration template
			const template = TUNNEL_TEMPLATE.replaceAll('{HOST}', tunnelUrl ?? '')
				.replaceAll('{HOST_PORT}', tunnel.port?.toString() ?? '')
				.replaceAll('{LISTEN_PORT}', tunnel?.port_socks?.toString() ?? '')
				.replaceAll('{KEY}', btoa(keys.privateKey));

			setTunnelResult({ tunnel, template });
			setModalState('success');
		} catch (error) {
			const message =
				error instanceof Error ? error.message : 'Failed to create tunnel';
			setErrorMessage(message);
			setModalState('error');
		}
	}, [onCreateTunnel, tunnelUrl]);

	const handleCopyConfiguration = useCallback(() => {
		if (tunnelResult) {
			clipboard.copy(tunnelResult.template);
			showNotification({
				title: 'Configuration copied',
				color: 'green',
				message:
					'The docker compose configuration has been copied to your clipboard',
			});
		}
	}, [clipboard, tunnelResult]);

	const handleClose = useCallback(() => {
		closeAllModals();
	}, []);

	const handleRetry = useCallback(() => {
		setModalState('initial');
		setErrorMessage('');
		setTunnelResult(null);
	}, []);

	// Render success state
	if (modalState === 'success' && tunnelResult) {
		return (
			<Stack spacing={theme.spacing.lg} mt={0}>
				<Stack spacing={theme.spacing.md}>
					<Stack spacing={theme.spacing.xs}>
						<Text size="md" weight="semibold" color="text/primary/default">
							Docker Compose Configuration
						</Text>
						<Text size="sm" color="text/secondary/default">
							Your reverse tunnel has been successfully created. Copy the
							configuration below and use it in your Docker Compose setup.{' '}
							<Anchor
								component="a"
								href="https://docs.secoda.com/integrations/connecting-via-tunnels/connecting-via-secoda-agent-reverse-ssh-tunnel"
								target="_blank"
								rel="noopener noreferrer"
								size="sm"
							>
								View documentation
							</Anchor>
						</Text>
					</Stack>

					<Textarea minRows={8} value={tunnelResult?.template} readOnly />
				</Stack>

				<Group
					spacing={theme.spacing.sm}
					sx={{
						justifyContent: 'flex-end',
						paddingTop: theme.spacing.sm,
						borderTop: `1px solid ${theme.other.getColor('border/secondary/default')}`,
					}}
				>
					<Button variant="tertiary" onClick={handleClose}>
						Close
					</Button>
					<Button variant="primary" onClick={handleCopyConfiguration}>
						Copy Configuration
					</Button>
				</Group>
			</Stack>
		);
	}

	// Render error state
	if (modalState === 'error') {
		return (
			<Stack spacing={theme.spacing.md} mt={0}>
				<Text color="text/critical/default">
					Failed to create reverse tunnel: {errorMessage}
				</Text>
				<Group
					spacing={theme.spacing.sm}
					sx={{
						justifyContent: 'flex-end',
					}}
				>
					<Button variant="tertiary" onClick={handleRetry}>
						Retry
					</Button>
					<Button variant="tertiary" onClick={handleClose}>
						Cancel
					</Button>
				</Group>
			</Stack>
		);
	}

	// Render initial/loading state
	return (
		<Stack spacing={theme.spacing.md} mt={0}>
			<Text>
				This configuration will be displayed once and cannot be retrieved later.
				For security reasons, the generated private key is not stored on our
				servers. When you're ready to proceed, click the button below to create
				the reverse tunnel.
			</Text>
			<Group
				spacing={theme.spacing.sm}
				sx={{
					justifyContent: 'flex-end',
				}}
			>
				<Button
					variant="tertiary"
					onClick={handleClose}
					disabled={modalState === 'loading'}
				>
					Cancel
				</Button>
				<Button
					onClick={handleCreateTunnel}
					loading={modalState === 'loading'}
					disabled={modalState === 'loading'}
				>
					Create reverse tunnel
				</Button>
			</Group>
		</Stack>
	);
}

export default CreateReverseTunnelModal;
